import useIsMobile from '@/util/useIsMobile'
import { createClient } from 'contentful'
import Head from 'next/head'
import { useRef, useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useGetCensored } from 'src/hooks/useGetCensored'
import { useRouter } from 'next/router'
import Checkout from '@/components/checkout'
import { tier } from '@/util/types'
import { useRecoilValue } from 'recoil'
import { subDataState } from '@/util/states'
import { urlParser } from '@/util/imgHelpers'

export async function getServerSideProps() {
  const spaceId = process.env.CONTENTFUL_SPACE_ID
  const accessToken = process.env.CONTENTFUL_ACCESS_KEY

  if (!spaceId || !accessToken) {
    throw '.env.local variables are not setup correctly.'
  }

  const client = createClient({
    space: spaceId,
    accessToken: accessToken,
    environment: process.env.CONTENTFUL_ENV,
  })

  const subTypeEntriesPromise = client.getEntries({
    content_type: 'subscriptionTypes',
  })

  const subTypeEntries = await subTypeEntriesPromise

  return {
    props: {
      subTypeEntries: subTypeEntries.items
    }
  }
}

export default function Subscribe({
  subTypeEntries
}: {
  subTypeEntries: Record<string, any>
}) {
  const isMobile = useIsMobile()
  const censoredContent = useGetCensored({ limit: 8 })
  const pricingRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const subData = useRecoilValue(subDataState)

  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  if (isMobile === undefined) return null
  if (subData === 'pending') return null

  // If user is subscribed, show the management view
  if (subData.isSubbed) {
    return (
      <>
        <Head>
          <title>Manage Subscription — Beyond The Pines</title>
          <meta name="description" content="Manage your Beyond The Pines subscription" />
        </Head>
        <ManagePageWrapper>
          <ManageHeader>
            <CurrentPlanBadge>
              <CurrentPlanIcon 
                src={`/images/${subData.tier?.toLowerCase()}-icon.png`} 
                alt={subData.tier} 
              />
              <CurrentPlanInfo>
                <CurrentPlanLabel>Current Plan</CurrentPlanLabel>
                <CurrentPlanName>
                  {subData.tier} · {subData.monthlyOrYearly}
                </CurrentPlanName>
              </CurrentPlanInfo>
            </CurrentPlanBadge>
            <ManageTitle>Manage Your Subscription</ManageTitle>
            <ManageSubtitle>
              Upgrade or change your plan below. Changes take effect on your next billing cycle.
            </ManageSubtitle>
          </ManageHeader>

          <ManagePricingGrid>
            {subTypeEntries.map((entry: any) => {
              const isCurrentTier = entry.fields?.name === subData.tier
              return (
                <ManagePricingCard 
                  key={entry.sys.id} 
                  isCurrentTier={isCurrentTier}
                  featured={entry.fields?.name === 'Beyond'}
                >
                  {isCurrentTier && (
                    <CurrentBadge>Current Plan</CurrentBadge>
                  )}
                  {!isCurrentTier && entry.fields?.name === 'Beyond' && (
                    <UpgradeBadge>Upgrade</UpgradeBadge>
                  )}
                  <PricingIcon 
                    src={`/images/${entry.fields?.name?.toLowerCase()}-icon.png`} 
                    alt={entry.fields?.name}
                  />
                  <PricingTierName>{entry.fields?.name}</PricingTierName>
                  <PricingDescription>
                    {entry.fields?.description?.content?.map((obj: any, i: number) => (
                      <p key={i}>{obj.content[0]?.value}</p>
                    ))}
                  </PricingDescription>
                  <CheckoutWrapper>
                    <Checkout
                      planName={entry.fields?.name as tier}
                      monthlyPrice={entry.fields?.pricePerMonth || ''}
                      yearlyPrice={entry.fields?.pricePerYear || ''}
                    />
                  </CheckoutWrapper>
                </ManagePricingCard>
              )
            })}
          </ManagePricingGrid>

          <ManageActions>
            <ActionButton onClick={() => router.push('/issues')}>
              View Your Content →
            </ActionButton>
            <ActionButtonSecondary onClick={() => router.push('/my-account')}>
              Account Settings
            </ActionButtonSecondary>
          </ManageActions>
        </ManagePageWrapper>
      </>
    )
  }
  
  return (
    <>
      <Head>
        <title>Subscribe to Beyond The Pines Magazine</title>
        <meta
          name="description"
          content="Subscribe to Beyond the Pines Magazine. Beyond The Pines Magazine: breathtaking photography and visual provocation that stimulates thoughts, ideas, & conversations."
        />
        <meta
          name="keywords"
          content="Beyond The Pines, Beyond The Pines Magazine, Beyond The Pines Mag, magazine, mag, BTP, BTP magazine, BTP mag, subscribe, subscription"
        />
      </Head>
      <PageWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroVideo autoPlay muted loop playsInline>
            <source src="https://videos.ctfassets.net/rxdl0mk5gigv/2TTvwmOtvuFH9IwWjreqBE/17cf83e32ced4541be23fcc4d2fadffe/fuji-4.mp4" type="video/mp4" />
          </HeroVideo>
          <HeroOverlay />
          <HeroContent>
            <HeroTagline>Beyond The Pines</HeroTagline>
            <HeroTitle>
              In Pixels We See <br />
              <GradientText>Time & Life</GradientText>
            </HeroTitle>
            <HeroDescription>
              Passion sees the photo before it&apos;s captured. 
              The value of a picture is not proportionate to the trouble it costs — but to the poetry it contains.
            </HeroDescription>
            <HeroButtonGroup>
              <PrimaryButton onClick={scrollToPricing}>
                Enter The Experience
              </PrimaryButton>
            </HeroButtonGroup>
          </HeroContent>
        </HeroSection>

        {/* Content Preview Section */}
        <PreviewSection>
          <SectionHeader>
            <SectionTagline>Latest Drops</SectionTagline>
            <SectionTitle>Inspired Vision</SectionTitle>
            <SectionSubtitle>
              Creativity&apos;s whisper, captured. 
              Here&apos;s what members are experiencing right now.
            </SectionSubtitle>
          </SectionHeader>
          
          <PreviewGrid>
            {censoredContent.map((item: any, index: number) => (
              <PreviewCard key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <PreviewImageWrapper>
                  <PreviewImage 
                    src={urlParser(item.image, { width: 800, quality: 80, format: 'webp' })} 
                    alt={item.title}
                  />
                  <PreviewGradient />
                  <MemberBadge>
                    <span>🔒 Members Only</span>
                  </MemberBadge>
                </PreviewImageWrapper>
                <PreviewTitle>{item.title}</PreviewTitle>
              </PreviewCard>
            ))}
          </PreviewGrid>

          <PreviewCTA>
            <CTAText>Ready to see the poetry?</CTAText>
            <PrimaryButton onClick={scrollToPricing}>
              Join The Experience
            </PrimaryButton>
          </PreviewCTA>
        </PreviewSection>

        {/* Pricing Section */}
        <PricingSection ref={pricingRef}>
          <SectionHeader light>
            <SectionTagline>Choose Your Tier</SectionTagline>
            <SectionTitle>Join The Experience</SectionTitle>
            <SectionSubtitle>
              It&apos;s best enjoyed by someone just like you. Cancel anytime.
            </SectionSubtitle>
          </SectionHeader>

          <PricingGrid>
            {subTypeEntries.map((entry: any, index: number) => (
              <PricingCard 
                key={entry.sys.id} 
                featured={entry.fields?.name === 'Beyond'}
              >
                {entry.fields?.name === 'Beyond' && (
                  <FeaturedBadge>Most Popular</FeaturedBadge>
                )}
                <PricingIcon 
                  src={`/images/${entry.fields?.name?.toLowerCase()}-icon.png`} 
                  alt={entry.fields?.name}
                />
                <PricingTierName>{entry.fields?.name}</PricingTierName>
                <PricingDescription>
                  {entry.fields?.description?.content?.map((obj: any, i: number) => (
                    <p key={i}>{obj.content[0]?.value}</p>
                  ))}
                </PricingDescription>
                <CheckoutWrapper>
                  <Checkout
                    planName={entry.fields?.name as tier}
                    monthlyPrice={entry.fields?.pricePerMonth || ''}
                    yearlyPrice={entry.fields?.pricePerYear || ''}
                  />
                </CheckoutWrapper>
              </PricingCard>
            ))}
          </PricingGrid>
        </PricingSection>

        {/* Impact Section */}
        <ImpactSection>
          <ImpactContent>
            <SectionTagline>Our Impact</SectionTagline>
            <ImpactTitle>Subscribe & Give Back</ImpactTitle>
            <ImpactSubtitle>
              Every membership makes a real difference. 
              Together, we&apos;re building a better world.
            </ImpactSubtitle>
            
            <ImpactGrid>
              <ImpactCard>
                <ImpactIcon src="/images/btp-leaf.png" alt="Trees" />
                <ImpactNumber>22,000+</ImpactNumber>
                <ImpactLabel>Trees Planted</ImpactLabel>
              </ImpactCard>
              <ImpactCard>
                <ImpactIcon src="/images/btp-school.png" alt="Schools" />
                <ImpactNumber>1</ImpactNumber>
                <ImpactLabel>School Built</ImpactLabel>
              </ImpactCard>
            </ImpactGrid>
          </ImpactContent>
        </ImpactSection>

        {/* Final CTA Section */}
        <FinalCTASection>
          <FinalCTAVideo autoPlay muted loop playsInline>
            <source src="https://videos.ctfassets.net/rxdl0mk5gigv/7zSI6i4s2ZSMJyvF3PYBzH/eb0506e2ba6fafcc08a7affa8e2729d0/fuji-2.mp4" type="video/mp4" />
          </FinalCTAVideo>
          <FinalCTAOverlay />
          <FinalCTAContent>
            <FinalCTATitle>Welcome to Beyond The Pines</FinalCTATitle>
            <FinalCTASubtitle>
              A digital experience by Badboi. Best enjoyed by someone just like you.
            </FinalCTASubtitle>
            <PrimaryButton large onClick={scrollToPricing}>
              Begin Your Journey
            </PrimaryButton>
          </FinalCTAContent>
        </FinalCTASection>
      </PageWrapper>
    </>
  )
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`

// Styled Components
const PageWrapper = styled.div`
  background: #0a0a0a;
  min-height: 100vh;
  overflow-x: hidden;
`

const HeroSection = styled.section`
  position: relative;
  min-height: 85vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 24px 80px;
  background: #0a0a0a;
  overflow: hidden;
`

const HeroVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  z-index: 0;
`

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0.7) 0%,
    rgba(10, 10, 10, 0.5) 50%,
    rgba(10, 10, 10, 0.8) 100%
  );
  z-index: 1;
`

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 800px;
  animation: ${fadeIn} 1s ease-out;
`

const HeroTagline = styled.span`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #6B8E23;
  margin-bottom: 20px;
  padding: 8px 20px;
  border: 1px solid rgba(107, 142, 35, 0.3);
  border-radius: 30px;
`

const HeroTitle = styled.h1`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(40px, 8vw, 80px);
  font-weight: 400;
  color: white;
  line-height: 1.1;
  margin: 0 0 24px;
  letter-spacing: -0.02em;
`

const GradientText = styled.span`
  -webkit-text-stroke: 2px rgba(255, 255, 255, 0.9);
  -webkit-text-fill-color: transparent;
  color: transparent;
`

const HeroDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin: 0 0 40px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 600px) {
    font-size: 16px;
  }
`

const HeroButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;

  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: center;
  }
`

const PrimaryButton = styled.button<{ large?: boolean }>`
  background: linear-gradient(135deg, #556B2F 0%, #6B8E23 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: ${props => props.large ? '16px' : '14px'};
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: ${props => props.large ? '18px 40px' : '14px 32px'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(85, 107, 47, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  padding: 14px 32px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.05);
  }
`

// Preview Section
const PreviewSection = styled.section`
  padding: 100px 24px;
  background: #0a0a0a;

  @media (min-width: 800px) {
    padding: 70px 48px;
  }
`

const SectionHeader = styled.div<{ light?: boolean }>`
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeIn} 0.6s ease-out;
`

const SectionTagline = styled.span`
  display: inline-block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #6B8E23;
  margin-bottom: 16px;
`

const SectionTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 48px);
  color: white;
  margin: 0 0 16px;
  letter-spacing: -0.01em;
`

const SectionSubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
`

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto 60px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`

const PreviewCard = styled.div`
  animation: ${fadeIn} 0.5s ease-out both;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`

const PreviewImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 12px;
  overflow: hidden;
  background: #151515;
`

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.5s ease;

  ${PreviewCard}:hover & {
    transform: scale(1.05);
  }
`

const PreviewGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.7) 100%);
  pointer-events: none;
`

const MemberBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 6px;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 10px;
  color: white;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`

// Keep for backwards compatibility but no longer used
const LockBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`

const LockIcon = styled.span`
  font-size: 24px;
  animation: ${float} 3s ease-in-out infinite;
`

const PreviewTitle = styled.h3`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: white;
  text-align: center;
  margin-top: 12px;
  padding: 0 4px;
  opacity: 0.8;

  @media (min-width: 800px) {
    font-size: 15px;
  }
`

const PreviewCTA = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`

const CTAText = styled.span`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
`

// Value Section
const ValueSection = styled.section`
  padding: 100px 24px;
  background: linear-gradient(180deg, #0a0a0a 0%, #111 100%);

  @media (min-width: 800px) {
    padding: 120px 48px;
  }
`

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;

  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const ValueCard = styled.div`
  text-align: center;
  padding: 32px 24px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(107, 142, 35, 0.3);
    transform: translateY(-4px);
  }
`

const ValueIcon = styled.span`
  font-size: 40px;
  display: block;
  margin-bottom: 16px;
`

const ValueTitle = styled.h3`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 16px;
  color: white;
  margin: 0 0 8px;
  letter-spacing: 0.5px;
`

const ValueDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0;
`

// Pricing Section
const PricingSection = styled.section`
  padding: 50px 24px;
  background: #0a0a0a;

  @media (min-width: 800px) {
    padding: 60px 48px;
  }
`

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 900px;
  margin: 0 auto;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const PricingCard = styled.div<{ featured?: boolean }>`
  position: relative;
  padding: 40px 32px;
  background: ${props => props.featured 
    ? 'linear-gradient(135deg, rgba(107, 142, 35, 0.15) 0%, rgba(85, 107, 47, 0.08) 100%)' 
    : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.featured 
    ? 'rgba(107, 142, 35, 0.4)' 
    : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 20px;
  text-align: center;
  transition: border-color 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    border-color: ${props => props.featured 
      ? 'rgba(107, 142, 35, 0.6)' 
      : 'rgba(255, 255, 255, 0.15)'};
  }
`

const FeaturedBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #6B8E23 0%, #556B2F 100%);
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 20px;
`

const PricingIcon = styled.img`
  width: 70px;
  height: 70px;
  object-fit: contain;
  margin: 0 auto 20px;
`

const PricingTierName = styled.h3`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 28px;
  color: white;
  margin: 0 0 16px;
  letter-spacing: 2px;
  text-transform: uppercase;
`

const PricingDescription = styled.div`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.7;
  margin-bottom: 24px;
  flex-grow: 1;

  p {
    margin: 0 0 8px;
  }
`

const CheckoutWrapper = styled.div`
  margin-top: auto;
  width: 100%;
`

// Impact Section
const ImpactSection = styled.section`
  padding: 50px 24px;
  background: linear-gradient(180deg, #111 0%, #0a0a0a 100%);

  @media (min-width: 800px) {
    padding: 60px 48px;
  }
`

const ImpactContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`

const ImpactTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 42px);
  color: white;
  margin: 0 0 16px;
`

const ImpactSubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0 0 50px;
`

const ImpactGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 60px;
  flex-wrap: wrap;

  @media (max-width: 500px) {
    gap: 40px;
  }
`

const ImpactCard = styled.div`
  text-align: center;
`

const ImpactIcon = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  margin-bottom: 16px;
`

const ImpactNumber = styled.div`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 48px;
  color: #6B8E23;
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: 500px) {
    font-size: 36px;
  }
`

const ImpactLabel = styled.div`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 1px;
`

// Final CTA Section
const FinalCTASection = styled.section`
  padding: 120px 24px;
  background: #0a0a0a;
  position: relative;
  overflow: hidden;
`

const FinalCTAVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  z-index: 0;
`

const FinalCTAOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0.8) 0%,
    rgba(10, 10, 10, 0.6) 50%,
    rgba(10, 10, 10, 0.85) 100%
  );
  z-index: 1;
`

const FinalCTAContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
`

const FinalCTATitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(32px, 6vw, 52px);
  color: white;
  margin: 0 0 16px;
`

const FinalCTASubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 40px;
`

// Subscription Management Styles
const ManagePageWrapper = styled.div`
  background: #0a0a0a;
  min-height: 100vh;
  padding: 60px 24px 100px;

  @media (min-width: 800px) {
    padding: 80px 48px 120px;
  }
`

const ManageHeader = styled.div`
  text-align: center;
  max-width: 600px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 0.6s ease-out;
`

const CurrentPlanBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  background: rgba(107, 142, 35, 0.15);
  border: 1px solid rgba(107, 142, 35, 0.3);
  border-radius: 16px;
  padding: 12px 24px 12px 16px;
  margin-bottom: 32px;
`

const CurrentPlanIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`

const CurrentPlanInfo = styled.div`
  text-align: left;
`

const CurrentPlanLabel = styled.span`
  display: block;
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 2px;
`

const CurrentPlanName = styled.span`
  display: block;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 16px;
  color: white;
`

const ManageTitle = styled.h1`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: clamp(28px, 5vw, 40px);
  color: white;
  margin: 0 0 16px;
  letter-spacing: -0.01em;
`

const ManageSubtitle = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  margin: 0;
`

const ManagePricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 900px;
  margin: 0 auto 48px;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const ManagePricingCard = styled.div<{ isCurrentTier?: boolean; featured?: boolean }>`
  position: relative;
  padding: 40px 32px;
  background: ${props => props.isCurrentTier 
    ? 'linear-gradient(135deg, rgba(107, 142, 35, 0.2) 0%, rgba(85, 107, 47, 0.1) 100%)' 
    : props.featured
      ? 'linear-gradient(135deg, rgba(107, 142, 35, 0.1) 0%, rgba(85, 107, 47, 0.05) 100%)'
      : 'rgba(255, 255, 255, 0.02)'};
  border: 2px solid ${props => props.isCurrentTier 
    ? 'rgba(107, 142, 35, 0.6)' 
    : props.featured
      ? 'rgba(107, 142, 35, 0.3)'
      : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 20px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${props => props.isCurrentTier 
      ? 'rgba(107, 142, 35, 0.8)' 
      : 'rgba(107, 142, 35, 0.5)'};
  }
`

const CurrentBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #6B8E23;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 20px;
`

const UpgradeBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #DAA520 0%, #B8860B 100%);
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 20px;
`

const ManageActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  max-width: 500px;
  margin: 0 auto;
`

const ActionButton = styled.button`
  background: linear-gradient(135deg, #556B2F 0%, #6B8E23 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 14px;
  letter-spacing: 1px;
  padding: 14px 28px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(85, 107, 47, 0.4);
  }
`

const ActionButtonSecondary = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 14px;
  padding: 14px 28px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
  }
`
